import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import DashboardHeader from './components/DashboardHeader';
import DashboardHome from './components/DashboardHome';
import JobGathering from './components/JobGathering';
import TeamLeadTasks from './components/TeamLeadTasks';
import RecruiterTasks from './components/RecruiterTasks';
import CreatedTasksList from './components/CreatedTasksList';
import AdminTasks from './components/AdminTasks';
import RecruiterAnalytics from './components/RecruiterAnalytics';
import TeamLeadAnalytics from './components/TeamLeadAnalytics';
import AdminAnalytics from './components/AdminAnalytics';
import RecruiterInterviews from './components/RecruiterInterviews';
import TeamLeadInterviews from './components/TeamLeadInterviews';
import AdminInterviews from './components/AdminInterviews';
import RecruiterCandidateDetails from './components/RecruiterCandidateDetails';
import TeamLeadCandidateDetails from './components/TeamLeadCandidateDetails';
import AdminCandidateDetails from './components/AdminCandidateDetails';
import CompanyDetails from './components/CompanyDetails';
import AttendanceManagement from './components/AttendanceManagement';
import AccountControl from './components/AccountControl';
import NamedFreelancerDetails from './components/NamedFreelancerDetails';
import { UNAUTHORIZED_EVENT, MOBILE_BLOCKED_EVENT } from './utils/authFetch'; // also installs the Authorization: Bearer fetch wrapper
import { isMobileDevice, classifyDevice } from './utils/deviceDetection';
import { matchesDateFilter, todayDateFilter, resolveDateRange, parseDateValue } from './components/DateFilter';

const ProtectedRoute = ({ user, allowedRole, allowedCheck, children }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to="/" replace />;
  }
  // allowedCheck: for routes that aren't a single exact role — e.g. Attendance,
  // which is Admin always, plus only the one Team Lead flagged can_manage_attendance.
  if (allowedCheck && !allowedCheck(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const RootRedirect = ({ user }) => {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'teamLead') return <Navigate to="/team-lead" replace />;
  if (user.role === 'recruiter') return <Navigate to="/recruiter" replace />;
  return <Navigate to="/login" replace />;
};
const API = process.env.REACT_APP_BACKEND_API_URL;
const APP_VERSION = '1.4';
// Item #3: ONE combined Date Filter — matches either Assigned Date or Reassigned Date.
// ============================================================
// HISTORICAL TASK SNAPSHOT
// ============================================================
// The DB row contains the CURRENT task state.
// For a historical Day-wise date, reconstruct the task state
// from its event history.
//
// IMPORTANT:
// - assigned_date may be changed by carry-forward.
// - reassigned_date only tells us the latest reassignment.
// - events contains the actual historical timeline.
// ============================================================

const createHistoricalTaskSnapshot = (task, selectedDate) => {
  if (!task || !selectedDate) { return null; }

  const events = Array.isArray(task.events) ? task.events.filter(event => event && event.created_at)
    .map(event => ({ ...event, _time: new Date(event.created_at).getTime() }))
    .filter(event => !Number.isNaN(event._time))
    .sort((a, b) => a._time - b._time)
    : [];

  if (events.length === 0) { return null; }

  // Convert an event timestamp into the same local calendar date
  // used by the application's DateFilter.
  const eventToDate = (event) => {
    const d = new Date(event.created_at);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // A task is visible on a historical date if it was created
  // on or before that date.
  const createdEvent = events.find(event =>
    String(event.action_type || '').trim().toLowerCase() === 'task created');

  if (!createdEvent) { return null; }

  const createdDate = eventToDate(createdEvent);

  if (!createdDate || createdDate > selectedDate) { return null; }

  // Start from the original creation state.
  let assignedTo = null;
  let status = 'Assigned';

  // Replay every meaningful historical event up to the END
  // of the selected calendar date.
  const endOfSelectedDate = `${selectedDate}T23:59:59.999`;

  for (const event of events) {
    const eventDate = eventToDate(event);
    if (!eventDate || eventDate > selectedDate) { continue; }

    const actionType = String(event.action_type || '').trim().toLowerCase();

    // ----------------------------------------
    // Initial assignment
    // ----------------------------------------
    if (actionType === 'task assigned') {
      if (event.new_value) {
        assignedTo = String(event.new_value).trim();
      }
      continue;
    }

    // ----------------------------------------
    // Reassignment
    // ----------------------------------------
    if (actionType === 'task reassigned' || actionType === 'recruiter changed') {
      if (event.new_value) {
        assignedTo = String(event.new_value).trim();
      }
      continue;
    }

    // ----------------------------------------
    // Status changes
    // ----------------------------------------
    if (actionType === 'status changed') {
      if (event.new_value) {
        status = String(event.new_value).trim();
      }
      continue;
    }
  }

  // If the task existed but assignment was not found,
  // fall back to current assignment only as a safety net.
  if (!assignedTo) { assignedTo = task.assigned_to || null; }

  return {
    ...task,
    // Historical values used by the Kanban layer.
    _historicalAssignedTo: assignedTo,
    _historicalStatus: status,
    // Historical date information.
    _historicalDate: selectedDate
  };
};


// ============================================================
// COMBINED TASK DATE FILTER
// ============================================================

const createCombinedDateFilterCondition = (filterState) => (task) => {
  if (!task || !filterState) { return false; }
  // ALL
  if (filterState.mode === 'All') { return true; }

  // Resolve the selected filter into an actual calendar range.
  const range = resolveDateRange(filterState);

  if (!range || !range.start || !range.end) {
    return matchesDateFilter(task, filterState, ['assigned_date', 'reassigned_date']);
  }

  // Historical reconstruction is required when the filter
  // represents exactly ONE calendar day.
  const isSingleDay = range.start === range.end;

  if (isSingleDay) {
    const historicalTask = createHistoricalTaskSnapshot(task, range.start);

    if (!historicalTask) { return false; }

    task._historicalAssignedTo = historicalTask._historicalAssignedTo;
    task._historicalStatus = historicalTask._historicalStatus;
    task._historicalDate = historicalTask._historicalDate;
    return true;
  }

  // Weekly / Monthly / Range continue using the existing
  // DateFilter behavior for now.
  return matchesDateFilter(task, filterState, ['assigned_date', 'reassigned_date']);
};

// Team Lead home (the 3/4-box screen). No Kanban board here anymore —
// Tasks now lives on its own route (/team-lead/tasks).
const TeamLeadDashboardPage = ({ user, activeSection, setActiveSection, handleLogout }) => {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
      <DashboardHeader
        user={user}
        titleText="Team Lead Dashboard"
        onLogout={handleLogout}
      />

      <DashboardHome
        user={user}
        activeSection={activeSection}
        onSectionSelect={setActiveSection}
        showJobGathering={true}
        onJobGatheringClick={() => navigate('/team-lead/job-gathering')}
        showCreatedTasks={true}
        onCreatedTasksClick={() => navigate('/team-lead/created-tasks')}
        onTasksClick={() => navigate('/team-lead/tasks')}
        showCandidateDetails={true}
        onCandidateDetailsClick={() => navigate('/team-lead/candidate-details')}
        showFreelancers={true}
        onFreelancersClick={() => navigate('/team-lead/named-freelancers')}
        showAttendance={!!user?.can_manage_attendance}
        onAttendanceClick={() => navigate('/team-lead/attendance')}
        onAnalyticsClick={() => navigate('/team-lead/analytics')}
        onInterviewsClick={() => navigate('/team-lead/interviews')}
      />
    </div>
  );
};

// Wraps TeamLeadTasks with the filter state it needs.
const TeamLeadTasksPage = ({ user, tasks, tlFilterState, handleTlFilterChange, handleUpdateTaskStatus, handleReassignTask, handleLogout, fetchTasks }) => {
  return (
    <TeamLeadTasks
      tasks={tasks}
      currentUser={user}
      filterState={tlFilterState}
      onFilterChange={handleTlFilterChange}
      filterCondition={createCombinedDateFilterCondition(tlFilterState)}
      onUpdateTaskStatus={handleUpdateTaskStatus}
      onReassignTask={handleReassignTask}
      onLogout={handleLogout}
      fetchTasks={fetchTasks}
    />
  );
};

// Recruiter home (boxes only). Tasks box navigates to /recruiter/tasks,
// same pattern as Team Lead's dashboard.
const RecruiterDashboardPage = ({ user, activeSection, setActiveSection, handleLogout }) => {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
      <DashboardHeader user={user} titleText="Recruiter Dashboard" onLogout={handleLogout} />

      <DashboardHome
        user={user}
        activeSection={activeSection}
        onSectionSelect={setActiveSection}
        showJobGathering={true}
        onJobGatheringClick={() => navigate('/recruiter/job-gathering')}
        onTasksClick={() => navigate('/recruiter/tasks')}
        showCandidateDetails={true}
        onCandidateDetailsClick={() => navigate('/recruiter/candidate-details')}
        showFreelancers={true}
        onFreelancersClick={() => navigate('/recruiter/named-freelancers')}
        onAnalyticsClick={() => navigate('/recruiter/analytics')}
        onInterviewsClick={() => navigate('/recruiter/interviews')}
      />
    </div>
  );
};

// Wraps RecruiterTasks with the filter state + status-update handler it needs.
const RecruiterTasksPage = ({ user, tasks, recruiterFilterState, handleRecruiterFilterChange, handleUpdateTaskStatus, handleLogout }) => {
  return (
    <RecruiterTasks
      tasks={tasks}
      filterState={recruiterFilterState}
      onFilterChange={handleRecruiterFilterChange}
      filterCondition={createCombinedDateFilterCondition(recruiterFilterState)}
      onUpdateTaskStatus={handleUpdateTaskStatus}
      currentUser={user}
      onLogout={handleLogout}
    />
  );
};

// Admin home (boxes only) — same Job Gathering / Created Tasks / Tasks / Interviews
// / Analytics layout as Team Lead, since Admin has full oversight of the same areas.
const AdminDashboardPage = ({ user, activeSection, setActiveSection, handleLogout }) => {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
      <DashboardHeader
        user={user}
        titleText="Leadership Dashboard"
        onLogout={handleLogout}
      />

      <DashboardHome
        user={user}
        activeSection={activeSection}
        onSectionSelect={setActiveSection}
        showJobGathering={true}
        onJobGatheringClick={() => navigate('/admin/job-gathering')}
        showCreatedTasks={true}
        onCreatedTasksClick={() => navigate('/admin/created-tasks')}
        onTasksClick={() => navigate('/admin/tasks')}
        showCandidateDetails={true}
        onCandidateDetailsClick={() => navigate('/admin/candidate-details')}
        showFreelancers={true}
        onFreelancersClick={() => navigate('/admin/named-freelancers')}
        onInterviewsClick={() => navigate('/admin/interviews')}
        showCompanyDetails={true}
        onCompanyDetailsClick={() => navigate('/admin/company-details')}
        showAttendance={true}
        onAttendanceClick={() => navigate('/admin/attendance')}
        showAccountControl={true}
        onAccountControlClick={() => navigate('/admin/account-control')}
        onAnalyticsClick={() => navigate('/admin/analytics')}
      />
    </div>
  );
};

// Wraps AdminTasks with the filter state + full task list (unfiltered) it needs.
const AdminTasksPage = ({ user, tasks, adminFilterState, handleAdminFilterChange, handleUpdateTaskStatus, handleReassignTask, handleDeleteTask, handleEndTask, handleLogout, fetchTasks }) => {
  return (
    <AdminTasks
      tasks={tasks}
      currentUser={user}
      filterState={adminFilterState}
      onFilterChange={handleAdminFilterChange}
      filterCondition={createCombinedDateFilterCondition(adminFilterState)}
      onUpdateTaskStatus={handleUpdateTaskStatus}
      onReassignTask={handleReassignTask}
      onDeleteTask={handleDeleteTask}
      onEndTask={handleEndTask}
      onLogout={handleLogout}
      fetchTasks={fetchTasks}
    />
  );
};


const MobileAccessDenied = ({ onLogout, reasons = [] }) => {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: '#f4f6f8',
        fontFamily: 'Inter, sans-serif',
        textAlign: 'center'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          boxSizing: 'border-box',
          background: '#ffffff',
          padding: '32px 24px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 20px',
            borderRadius: '50%',
            background: '#fee2e2',
            color: '#b91c1c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '36px',
            fontWeight: '700'
          }}
        >
          !
        </div>

        <h1
          style={{
            margin: '0 0 12px',
            fontSize: '24px',
            color: '#111827'
          }}
        >
          Access Denied
        </h1>

        <p
          style={{
            margin: '0 0 24px',
            color: '#4b5563',
            fontSize: '15px',
            lineHeight: 1.7
          }}
        >
          Mobile access is restricted for your account.
          Please use a desktop or laptop computer to access
          Recruiter360.
        </p>

        {reasons.length > 0 && (
          <p
            data-testid="device-reasons"
            style={{ margin: '0 0 20px', color: '#9ca3af', fontSize: '11px', lineHeight: 1.5, wordBreak: 'break-word' }}
          >
            Detected: {reasons.join('; ')}. If this is a laptop or desktop, please send this line to your administrator.
          </p>
        )}

        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: '#111827',
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = sessionStorage.getItem('user_ac');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  // Mobile-access policy: Admin may use any device; Team Lead / Recruiter may NOT use a
  // phone or tablet, even with "Desktop site" turned on. Detection is NOT based on
  // viewport width or the User-Agent (both change in Desktop-site mode) — see
  // utils/deviceDetection.js. The server enforces the same rule with 403 on every API.
  const [serverSaysMobile, setServerSaysMobile] = useState(false);
  const [serverReasons, setServerReasons] = useState([]);
  const [mobileDevice, setMobileDevice] = useState(() => isMobileDevice());
  useEffect(() => {
    const recheck = () => setMobileDevice(isMobileDevice());
    window.addEventListener('resize', recheck);
    window.addEventListener('orientationchange', recheck);
    const onBlocked = (e) => { setServerSaysMobile(true); setServerReasons((e && e.detail && e.detail.reasons) || []); };
    window.addEventListener(MOBILE_BLOCKED_EVENT, onBlocked);
    return () => {
      window.removeEventListener('resize', recheck);
      window.removeEventListener('orientationchange', recheck);
      window.removeEventListener(MOBILE_BLOCKED_EVENT, onBlocked);
    };
  }, []);
  const isRestrictedMobile = !!user && user.role !== 'admin' && (mobileDevice || serverSaysMobile);

  const [tasks, setTasks] = useState([]);

  // Controls which of the boxes (Tasks / Interviews / Analytics & Reports)
  // is currently expanded on dashboards that still use inline toggling (Recruiter).
  const [activeSection, setActiveSection] = useState(null);

  // Item #1: every date filter defaults to today, not "All".
  const [tlFilterState, setTlFilterState] = useState(() => {
    // Always open on Day-wise = TODAY (a stale saved filter from an earlier
    // visit/day must not override the default).
    return todayDateFilter();
  });
  const [recruiterFilterState, setRecruiterFilterState] = useState(() => {
    // Always open on Day-wise = TODAY (a stale saved filter from an earlier
    // visit/day must not override the default).
    return todayDateFilter();
  });

  const [adminFilterState, setAdminFilterState] = useState(() => {
    // Always open on Day-wise = TODAY (a stale saved filter from an earlier
    // visit/day must not override the default).
    return todayDateFilter();
  });
  const handleTlFilterChange = (newState) => {
    setTlFilterState(newState);
    sessionStorage.setItem('recruiter360_tl_filter', JSON.stringify(newState));
  };
  const handleRecruiterFilterChange = (newState) => {
    setRecruiterFilterState(newState);
    sessionStorage.setItem('recruiter360_recruiter_filter', JSON.stringify(newState));
  };

  const handleAdminFilterChange = (newState) => {
    setAdminFilterState(newState);
    sessionStorage.setItem('recruiter360_admin_filter', JSON.stringify(newState));
  };

  const fetchTasks = useCallback(async () => {
    if (!user) return;

    const endpoint = (user.role === 'admin' || user.role === 'teamLead')
      ? `${API}/api/tasks` // no filter — Team Lead/Admin see everything (item #11)
      : `${API}/api/tasks?assigned_to=${encodeURIComponent(user.name)}`;

    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        alert(`Failed to fetch tasks: ${response.status}`);
        return null;
      }
      const data = await response.json();
      setTasks(data);
      // Important: the caller can now await completion of the task refresh.
      return data;
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      throw err;
    }
  }, [user]);

  useEffect(() => {
    // fetchTasks already logs; swallow the re-thrown error so a 401/403 doesn't
    // surface as an unhandled promise rejection.
    fetchTasks()?.catch(() => { });
  }, [fetchTasks]);

  useEffect(() => {
    setActiveSection(null);
  }, [user]);

  const handleLogin = (userData) => {
    setServerSaysMobile(false);
    setUser(userData);
    sessionStorage.setItem('user_ac', JSON.stringify(userData));
  };

  // authFetch dispatches this on any 401 (expired token, revoked/deleted account).
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setTasks([]);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const handleLogout = () => {
    setUser(null);
    setServerSaysMobile(false);
    sessionStorage.removeItem('user_ac');
    sessionStorage.removeItem('recruiter360_tl_filter');
    sessionStorage.removeItem('recruiter360_recruiter_filter');
    sessionStorage.removeItem('recruiter360_admin_filter');
  };

  const handleUpdateTaskStatus = async (taskId, newStatus, description = '', submittedTo = '', newAssignedTo = null) => {
    try {
      const response = await fetch(`${API}/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus || undefined,
          submission_description: description || undefined,
          submitted_to: submittedTo || undefined,
          new_assigned_to: newAssignedTo || undefined,
          updated_by: user?.name || 'Unknown User',
          updated_by_role: user?.role || null
        })
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to update task.');
        return null;
      }
      // Wait until the refreshed task list has completed.
      await fetchTasks();
      // Return the successful API response so callers can await this function.
      return data;
    } catch (err) {
      console.error('Failed to update task status:', err);
      alert('Unable to update the task. Please check your connection and try again.');
      return null;
    }
  };

  const handleReassignTask = (taskId, newAssignedTo) => {
    fetch(`${API}/api/tasks/${taskId}/reassign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        new_assigned_to: newAssignedTo,
        reassigned_by: user?.name || 'Unknown User',
        reassigned_by_role: user?.role || null
      })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert(data.error || 'Failed to reassign task.');
          return;
        }
        fetchTasks();
      })
      .catch(err => console.error('Failed to reassign task:', err));
  };

  // Admin-only: end a task from any non-completed status straight to Completed.
  const handleEndTask = (taskId, reason = '') => {
    fetch(`${API}/api/tasks/${taskId}/end`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || undefined })
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert(data.error || 'Failed to end task.');
          return;
        }
        fetchTasks();
      })
      .catch(err => console.error('Failed to end task:', err));
  };

  const handleDeleteTask = (taskId) => {
    fetch(`${API}/api/tasks/${taskId}?requester_role=${encodeURIComponent(user?.role || '')}`, { method: 'DELETE' })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert(data.error || 'Failed to delete task.');
          return;
        }
        fetchTasks();
      })
      .catch(err => console.error('Failed to delete task:', err));
  };

  return (

    <>
      <div style={{
        position: 'fixed', top: '8px', right: '12px', zIndex: 9999, background: '#f0f9ff', color: '#0369a1',
        padding: '3px 7px', borderRadius: '20px', fontSize: '15px', fontWeight: '600', gap: '6px',
        border: '1px solid #bae6fd', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', whiteSpace: 'nowrap',
        pointerEvents: 'none'
      }}>
        <span>Version {APP_VERSION}</span>
      </div>

  {isRestrictedMobile ? (
    <MobileAccessDenied onLogout={handleLogout} reasons={[...classifyDevice().reasons, ...serverReasons.filter(r => !classifyDevice().reasons.includes(r))]} />
  ) : (
    <Routes>
      <Route path="/" element={<RootRedirect user={user} />} />
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />}
      />

      <Route
        path="/team-lead"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <TeamLeadDashboardPage
              user={user}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              handleLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/tasks"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <TeamLeadTasksPage
              user={user}
              tasks={tasks}
              tlFilterState={tlFilterState}
              handleTlFilterChange={handleTlFilterChange}
              handleUpdateTaskStatus={handleUpdateTaskStatus}
              handleReassignTask={handleReassignTask}
              handleLogout={handleLogout}
              fetchTasks={fetchTasks}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/job-gathering"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <JobGathering user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/created-tasks"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <CreatedTasksList user={user} onLogout={handleLogout} refreshSharedTasks={fetchTasks} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/analytics"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <TeamLeadAnalytics user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/interviews"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <TeamLeadInterviews user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/team-lead/candidate-details"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <TeamLeadCandidateDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <RecruiterDashboardPage
              user={user}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              handleLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/tasks"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <RecruiterTasksPage
              user={user}
              tasks={tasks}
              recruiterFilterState={recruiterFilterState}
              handleRecruiterFilterChange={handleRecruiterFilterChange}
              handleUpdateTaskStatus={handleUpdateTaskStatus}
              handleLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/analytics"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <RecruiterAnalytics user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/interviews"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <RecruiterInterviews user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/candidate-details"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <RecruiterCandidateDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/job-gathering"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <JobGathering user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AdminDashboardPage
              user={user}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              handleLogout={handleLogout}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tasks"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AdminTasksPage
              user={user}
              tasks={tasks}
              adminFilterState={adminFilterState}
              handleAdminFilterChange={handleAdminFilterChange}
              handleUpdateTaskStatus={handleUpdateTaskStatus}
              handleReassignTask={handleReassignTask}
              handleDeleteTask={handleDeleteTask}
              handleEndTask={handleEndTask}
              handleLogout={handleLogout}
              fetchTasks={fetchTasks}
            />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/job-gathering"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <JobGathering user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/created-tasks"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <CreatedTasksList user={user} onLogout={handleLogout} refreshSharedTasks={fetchTasks} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AdminAnalytics user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/interviews"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AdminInterviews user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/candidate-details"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AdminCandidateDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/company-details"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <CompanyDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* Attendance: Admin always; Team Lead only when flagged can_manage_attendance. */}
      <Route
        path="/admin/attendance"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AttendanceManagement user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-lead/attendance"
        element={
          <ProtectedRoute user={user} allowedCheck={(u) => u.role === 'teamLead' && !!u.can_manage_attendance}>
            <AttendanceManagement user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* Account Control: Admin only (backend also enforces Admin-only on /api/admin/users*). */}
      <Route
        path="/admin/account-control"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <AccountControl user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      {/* Named Freelancer Details: every role (separate from Candidate Details). */}
      <Route
        path="/team-lead/named-freelancers"
        element={
          <ProtectedRoute user={user} allowedRole="teamLead">
            <NamedFreelancerDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recruiter/named-freelancers"
        element={
          <ProtectedRoute user={user} allowedRole="recruiter">
            <NamedFreelancerDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/named-freelancers"
        element={
          <ProtectedRoute user={user} allowedRole="admin">
            <NamedFreelancerDetails user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )}
    </>
  );
}

export default App;